<template>
  <div class="server-area-wrapper">
    <!-- NOTIFICATIONS -->
    <vs-dropdown vs-custom-content vs-trigger-click class="cursor-pointer server-area-trigger">
        <div class="text-gray-500 flex items-center" :title="`${mergedUsers.length} active user(s)`">
            <feather-icon icon="UsersIcon" class="w-5 h-5 mr-2 navbar-icon" />
            <span>{{ mergedUsers.length }}</span>
        </div>
        <vs-dropdown-menu class="notification-dropdown dropdown-custom vx-navbar-dropdown server-connections-dropdown">
            <div class="notification-top text-center p-5 bg-primary text-white">
                <h3 class="text-white">Active Users</h3>
            </div>
            <div class="ml-2 mr-2 mt-3 mb-4 text-sm">
                <div v-if="mergedUsers.length === 0" class="text-gray-500 italic">
                    No active user.
                </div>
                <div v-for="u in mergedUsers" :key="u.userId" class="flex items-center justify-between mt-2">
                    <span class="flex items-center px-2 py-1 rounded"
                          :class="isOnline(u.userId) ? 'text-green-500 bg-green-100' : 'text-gray-500 bg-gray-100'">
                        <feather-icon icon="UserIcon" class="w-4 h-4 mr-2" />
                        <span class="font-semibold">{{ u.username }}</span>
                    </span>
                    <span class="flex items-center">
                        <span class="text-gray-500 mr-2">{{ u.durationMs == null ? 'online' : formatDuration(u.durationMs) }}</span>
                        <span v-if="isBusy(u.userId)" class="text-xs text-orange-500">in a call</span>
                        <span v-else-if="!isOnline(u.userId)" class="text-xs text-gray-400" title="Not connected in real time">offline</span>
                    </span>
                </div>
            </div>

            <div class="active-users-divider"></div>
            <div class="notification-top text-center p-3 bg-primary text-white">
                <h4 class="text-white">Active Connections</h4>
            </div>
            <div class="vx-row mt-4 mb-4 ml-2 mr-2">
                <!-- Active IPs Grouped by State -->
                <div class="text-gray-500">
                    <div v-for="(group, ip) in groupedConnections" :key="ip" class="text-sm mt-4 mb-4">
                        <div class="font-semibold text-lg">
                             <a :href="'https://ipinfo.io/' + ip" target="_blank">{{ ip }}</a>
                        </div>
                        <div v-for="(status, state) in group" :key="state" class="flex items-center mt-2">
                            <span :class="getConnectionClass(state)" class="flex items-center">
                                <feather-icon :icon="getConnectionIcon(state)" class="w-5 h-5 mr-2" />
                                <span>{{ state }} - {{ group[state] }} processes</span>
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <div class="active-users-divider"></div>
            <div class="ml-2 mr-2 mt-2 mb-3">
                <button class="full-log-btn" @click="openFullLog">
                    <feather-icon icon="FileTextIcon" class="w-4 h-4 mr-2" />
                    <span>FULL LOG</span>
                </button>
            </div>
        </vs-dropdown-menu>
    </vs-dropdown>

    <full-audit-log :active.sync="showFullLog" />
  </div>
</template>

<script>
import VuePerfectScrollbar from 'vue-perfect-scrollbar';
import axios from "@/axios.js";
import moment from "moment";
import FullAuditLog from "./FullAuditLog.vue";
import meeting from "@/services/meeting.js";

export default {
    components: {
        VuePerfectScrollbar,
        FullAuditLog,
    },
    props: {
        activeConnectionsList: {
            type: Array,
            required: true
        },
    },
    data() {
        return {
            groupedConnections: {},
            activeUsers: [],
            activeUsersTimer: null,
            showFullLog: false
        };
    },
    computed: {
        // Real-time socket presence (green = has XYLO open right now). Calls
        // themselves are started from the navbar Calls panel (CallCenter.vue).
        myId() {
            return Number(localStorage.getItem('login-id')) || null;
        },
        onlineMap() {
            const m = new Map();
            (meeting.state.onlineUsers || []).forEach(u => m.set(u.userId, u));
            return m;
        },
        // Union of the HTTP "active users" (15-min window) and the real-time
        // socket presence. A socket-connected user who fell out of the 15-min
        // REST window (e.g. an idle broker) still appears here AND stays
        // callable. Online users first, then by recency.
        mergedUsers() {
            const byId = new Map();
            (this.activeUsers || []).forEach(u => byId.set(u.userId, {
                userId: u.userId, username: u.username, durationMs: u.durationMs
            }));
            (meeting.state.onlineUsers || []).forEach(u => {
                if (!byId.has(u.userId)) {
                    byId.set(u.userId, { userId: u.userId, username: u.username, durationMs: null });
                }
            });
            return Array.from(byId.values()).sort((a, b) => {
                const ao = this.onlineMap.has(a.userId) ? 0 : 1;
                const bo = this.onlineMap.has(b.userId) ? 0 : 1;
                if (ao !== bo) return ao - bo;
                return (b.durationMs || 0) - (a.durationMs || 0);
            });
        }
    },

    methods: {
        isOnline(id) { return this.onlineMap.has(id); },
        isBusy(id) { const u = this.onlineMap.get(id); return !!(u && u.busy); },

        openFullLog() {
            this.showFullLog = true;
        },

        groupConnections() {
            this.groupedConnections = {};

            this.activeConnectionsList.forEach(connection => {
                const ip = connection.remote.split(":")[0];  // On prend l'IP distante
                const state = this.getConnectionState(connection);

                if (!this.groupedConnections[ip]) {
                    this.groupedConnections[ip] = {};
                }

                if (!this.groupedConnections[ip][state]) {
                    this.groupedConnections[ip][state] = 0;
                }

                this.groupedConnections[ip][state] += 1;
            });
        },

        getConnectionState(connection) {
            if (this.isServerToClient(connection)) {
                return 'Active';
            } else if (this.isClientToServer(connection)) {
                return 'Collecting Data';
            } else if (this.isTimeWait(connection)) {
                return 'Waiting';
            } else {
                return 'Idle';
            }
        },

        isServerToClient(connection) {
            return connection.local.includes(":443") && connection.state === "ESTAB";
        },

        isClientToServer(connection) {
            return connection.remote.includes(":443") && connection.state === "ESTAB";
        },

        isTimeWait(connection) {
            return connection.state === "TIME-WAIT";
        },

        getConnectionClass(state) {
            if (state === 'Active') {
                return 'text-green-500 bg-green-100 px-2 py-1 rounded'; // Green background for Active User
            } else if (state === 'Collecting Data') {
                return 'text-blue-500 bg-blue-100 px-2 py-1 rounded'; // Blue background for Server Collecting Data
            } else if (state === 'Waiting') {
                return 'text-orange-500 bg-orange-100 px-2 py-1 rounded'; // Orange background for Waiting
            } else {
                return 'text-gray-500 bg-gray-100 px-2 py-1 rounded'; // Gray background for User Sleeping
            }
        },

        getConnectionIcon(state) {
            if (state === 'Active') {
                return 'CheckCircleIcon';
            } else if (state === 'Collecting Data') {
                return 'ServerIcon';
            } else if (state === 'Waiting') {
                return 'ClockIcon';
            } else {
                return 'AlertTriangleIcon';
            }
        },

        async fetchActiveUsers() {
            try {
                const res = await axios.get("/active-users");
                this.activeUsers = (res.data && res.data.users) || [];
            } catch (e) {
                // silent — section will simply show "No active user."
            }
        },

        formatDuration(ms) {
            const sec = Math.max(0, Math.floor((ms || 0) / 1000));
            if (sec < 60) return sec + "s";
            const min = Math.floor(sec / 60);
            if (min < 60) return min + "m";
            const hr = Math.floor(min / 60);
            const remMin = min % 60;
            return remMin > 0 ? `${hr}h${String(remMin).padStart(2, "0")}` : `${hr}h`;
        }
    },

    watch: {
        activeConnectionsList(newValue) {
            if (newValue.length > 0) {
                this.groupConnections();  // Appelle groupConnections seulement lorsque la liste a changé
            }
        }
    },

    mounted() {
        if (this.activeConnectionsList.length > 0) {
            this.groupConnections();
        }
        meeting.init(); // idempotent — ensures socket presence is available
        this.fetchActiveUsers();
        this.activeUsersTimer = setInterval(this.fetchActiveUsers, 30000);
    },

    beforeDestroy() {
        if (this.activeUsersTimer) clearInterval(this.activeUsersTimer);
    },

    created() {
        if (this.activeConnectionsList.length > 0) {
            this.groupConnections();
        }
    }
};
</script>

<style scoped>
/* Keep the dropdown trigger as a plain flex item in the navbar while still
   letting the FULL LOG popup live as a sibling of the dropdown. */
.server-area-wrapper {
    display: contents;
}


.full-log-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding: 8px 12px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    background: #fff;
    color: #374151;
    font-size: 0.78rem;
    font-weight: 600;
    letter-spacing: 0.03em;
    cursor: pointer;
    transition: background 0.15s, border-color 0.15s;
}
.full-log-btn:hover {
    background: #f3f4f6;
    border-color: #9ca3af;
}

.text-gray-500 {
    color: #6b7280;
}

.navbar-icon { color: #6b7280; }
.navbar-icon * { stroke: #6b7280; }

.text-green-500 {
    color: #10b981;
}

.text-blue-500 {
    color: #3b82f6;
}

.text-orange-500 {
    color: #f59e0b;
}

.text-gray-500 {
    color: #9ca3af;
}

/* Background colors */
.bg-green-100 {
    background-color: #d1fae5;
}

.bg-blue-100 {
    background-color: #bfdbfe;
}

.bg-orange-100 {
    background-color: #ffe4b3;
}

.bg-gray-100 {
    background-color: #f3f4f6;
}

/* Padding and rounded borders */
.px-2 {
    padding-left: 8px;
    padding-right: 8px;
}

.py-1 {
    padding-top: 4px;
    padding-bottom: 4px;
}

.rounded {
    border-radius: 8px;
}

.active-users-divider {
    height: 1px;
    background: #e5e7eb;
    margin: 8px 0;
}

/* Spacing between items */
.mt-2 {
    margin-top: 8px;
}

.mt-4 {
    margin-top: 16px;
}

.mb-4 {
    margin-bottom: 16px;
}

</style>